import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faHouse,
  faLayerGroup,
  faNetworkWired,
  faShapes,
  faUsersGear,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Flag,
  getConfiguration,
  getFlags,
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { NavLink as Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import { RequiredState } from '../common/enums';
import { DeveloperOrganizationContextProps } from '../common/providers/types';

interface DisabledLinkProps {
  path: string;
  title: string;
  icon?: IconProp;
  disabledTooltip?: string;
}

const SOLUTION_SUBMISSION_FLAG = 'solutionSubmission';

const DisabledLink: React.FunctionComponent<DisabledLinkProps> = ({
  path,
  title,
  icon,
  disabledTooltip,
}) => {
  const getTooltipContent = (toolTipStr: string) => {
    return <div className="w-52">{toolTipStr}</div>;
  };

  const disableOnClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
  };
  return (
    <Link
      className="p-3 rounded-lg flex flex-row bg-gray-100"
      data-testid={`${title}-disabled-link`}
      data-tip={disabledTooltip}
      onClick={disableOnClick}
      to={path}
    >
      <>
        <FontAwesomeIcon icon={icon as IconProp} />
        <div className="ml-5">{title}</div>
        <ReactTooltip
          effect="solid"
          getContent={getTooltipContent}
          multiline={true}
          place="right"
          resizeHide={true}
          scrollHide={true}
        />
      </>
    </Link>
  );
};

interface RouteItem {
  path: string;
  title: string;
  icon: IconProp;
  requiredState?: RequiredState;
  disabledTooltip?: string;
}

const routes: RouteItem[] = [
  {
    path: '/home',
    title: 'Dashboard',
    icon: faHouse,
  },
  {
    path: '/organization',
    title: 'Organization',
    icon: faNetworkWired,
  },
  {
    path: '/team',
    title: 'Team',
    icon: faUsersGear,

    requiredState: RequiredState.organization,
    disabledTooltip:
      'You must create or join an organization to create and manage teams.',
  },
  {
    path: '/application/root',
    title: 'Apps',
    icon: faShapes,

    requiredState: RequiredState.orgTenantAndTeam,
    disabledTooltip:
      'To work with apps, you must be a member of an organization, join a team, and have an active sandbox development environment tenant.',
  },
];

export const LeftNavigation: FunctionComponent = (): JSX.Element => {
  const { currentOrg, currentTenant } =
    useOrganization() as DeveloperOrganizationContextProps;
  const { currentTeam } = useTeam();
  const [featureFlags, setFeatureFlags] = useState<Flag[]>([]);
  const [menuItems, setMenuItems] = useState<RouteItem[]>([]);

  useEffect(() => {
    const getAppConfig = async () => {
      const config = await getConfiguration();
      const flags = getFlags(config.featureFlags);

      if (config.featureFlags && config.featureFlags.length > 0) {
        setFeatureFlags(flags);
      }
    };
    getAppConfig();
  }, []);

  useEffect(() => {
    const routesCopy = [...routes];

    if (
      featureFlags.find(
        (flag) => flag.name === SOLUTION_SUBMISSION_FLAG && flag.isActive
      )
    ) {
      routesCopy.push({
        path: '/solution/root',
        title: 'Solutions',
        icon: faLayerGroup,

        requiredState: RequiredState.orgTenantAndTeam,
        disabledTooltip:
          'To work with solutions, you must be a member of an organization, join a team, and have an active sandbox development environment tenant.',
      });
    }

    setMenuItems(routesCopy);
  }, [featureFlags]);

  return (
    <div className="w-64 h-full pt-5 px-2 border border-r-gray-200">
      {menuItems.map(
        ({ path, icon, title, requiredState, disabledTooltip }) => {
          if (
            requiredState &&
            requiredState === RequiredState.orgTenantAndTeam
          ) {
            if (!currentOrg || !currentTenant || !currentTeam) {
              return (
                <DisabledLink
                  disabledTooltip={disabledTooltip}
                  icon={icon}
                  key={path}
                  path={path}
                  title={title}
                />
              );
            }
          } else if (
            requiredState &&
            requiredState === RequiredState.organization
          ) {
            if (!currentOrg) {
              return (
                <DisabledLink
                  disabledTooltip={disabledTooltip}
                  icon={icon}
                  key={path}
                  path={path}
                  title={title}
                />
              );
            }
          }
          return (
            <Link
              className={({ isActive }) => {
                return `p-3 rounded-lg flex flex-row ${
                  isActive ? 'bg-indigo-100 text-primary' : ''
                }`;
              }}
              data-testid={`${title}-link`}
              key={path}
              to={path}
            >
              {({ isActive }) => {
                return (
                  <>
                    <FontAwesomeIcon
                      className={`${
                        isActive ? 'text-primary' : 'text-black'
                      } mt-1`}
                      icon={icon as IconProp}
                    />
                    <div className="ml-5 font-medium">{title}</div>
                  </>
                );
              }}
            </Link>
          );
        }
      )}
    </div>
  );
};
